import type { LocationDescriptor, NavigationScreenState } from '@gitbook/app-navigation';

import type { Location } from './history';
import type { NavigationState } from './types';

/**
 * A factory function that returns a method to get the navigation state from a location.
 */
export function getNavigationStateFactory({
    getNavigationScreenFromLocation,
    getLocationForNavigationScreen,
}: {
    getNavigationScreenFromLocation: (location: LocationDescriptor) => NavigationScreenState;
    getLocationForNavigationScreen: (screen: NavigationScreenState) => LocationDescriptor;
}) {
    return (location: Location): NavigationState => {
        const fromUrl = getNavigationScreenFromLocation(location);
        const fromBackground = (() => {
            if (!location.state?.background) {
                return null;
            }
            const parsed = new URL(location.state.background, window.location.origin);
            return getNavigationScreenFromLocation({
                pathname: parsed.pathname,
                hash: parsed.hash,
                search: parsed.search,
            });
        })();

        if (
            !fromBackground ||
            // Don't consider an overlay if same as the screen
            getLocationForNavigationScreen(fromBackground).pathname ===
                getLocationForNavigationScreen(fromUrl).pathname
        ) {
            return { screen: fromUrl };
        }

        return { screen: fromBackground, overlay: fromUrl };
    };
}
